import { AxiosResponse } from 'axios';
import { RequestClientInterfaceService } from './request-client.interface.service';
import {
    COGNITO_TOKEN_NAME,
    DEALER_PORTAL_URL,
    PERMISSIONS_TOKEN_NAME,
} from '../config';
import {
    DealerManagementTableData,
    IDealerManagementObject,
} from './models/dealer-management.service';
import { HttpMethod, HttpStatusCode } from '../utils/enums/http-method.enum';

/**
 * Class responsible for manage the API calls and data request of the App
 */
export class DealerManagementService {
    private readonly dealerPortalUrlStructure = `${DEALER_PORTAL_URL}`;
    private client: RequestClientInterfaceService;
    private headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: '',
    };
    /**
     * Instantiates a new ClientService
     */
    constructor() {
        this.client = new RequestClientInterfaceService();
    }

    public cancelRequest(): void {
        this.client.cancelReqeust();
    }
    /**
     * Retrieves the dealer users data
     * @param dealerId DealerId for dealers users
     */
    public async getUsers(dealerId: string): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getDealerUsers`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { dealerId, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Retrieves the dealer users data
     * @param username User email
     */
    public async resetMfa(username: string): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/resetMfa`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { username, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    public mapResponse(
        data: IDealerManagementObject[],
    ): DealerManagementTableData[] {
        return data
            .map((item) => {
                return {
                    userId: item.userId,
                    name: `${item.firstName} ${item.lastName}`,
                    email: item.loginId,
                    status: item.status,
                    isAdmin: item.isAdmin,
                    dealers: item.dealers.length,
                };
            })
            .sort((a, b) => {
                const nameA = a.name.toUpperCase();
                const nameB = b.name.toUpperCase();

                const A = -1;
                const B = 1;
                const defaultValue = 0;

                if (nameA < nameB) {
                    return A;
                }
                if (nameA > nameB) {
                    return B;
                }

                return defaultValue;
            });
    }

    /**
     * Retrieves the dealer users data
     * @param addUserRequest IAddDealerUserPayload object
     */
    public async addNewUser(
        addUserRequest: IAddDealerUserPayload,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/addDealerUser`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { ...addUserRequest, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Adds new field user
     * @param addFieldUser IAddFieldUserPayload object
     */
    public async addFieldUser(
        addFieldUserRequest: IAddFieldUserPayload,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/addFieldUser`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { ...addFieldUserRequest, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Updates field user
     * @param addFieldUser IUpdateFieldUserPayload object
     */
    public async updateFieldUser(
        updateFieldUserRequest: IUpdateFieldUserPayload,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/editFieldUser`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { ...updateFieldUserRequest, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Gets all field users
     */
    public async getAllFieldUsers(): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getAllFieldUsers`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    public async updateUserStatus(
        userId: string,
        status: number,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/updateUserStatus`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { status, userId: `${userId}`, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    public async editUser(
        editUserRequest: IEditDealerUserPayload,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/editDealerUser`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { ...editUserRequest, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    public async addDealerToGroup(
        dealerId: string,
        dealerGroupId: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/addDealerToGroup`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { dealerId, dealerGroupId, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    public async moveDealerToGroup(
        sourceDealerId: string,
        targetDealerId: string,
        preservePermissions: boolean,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/moveDealer`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                sourceDealerId,
                targetDealerId,
                preservePermissions,
                jwtToken: permissionsToken,
            },
        };
        return this.client.sendRequest(request);
    }

    public async deleteUser(
        userId: string,
        username: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/deleteDealerUser`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { username, userId: `${userId}`, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    public async sendPasswordResetLink(
        passwordResetLinkRequest: ISendPasswordResetLinkPayload,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/sendPasswordResetLink`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { ...passwordResetLinkRequest, jwtToken: permissionsToken },
            // validateStatus: this.validateLockedStatus,
        };
        return this.client.sendRequest(request);
    }

    public async sendForgotPasswordRequest(
        forgotPasswordRequest: IForgotPasswordPayload,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/forgotPassword`;

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
            },
            data: { ...forgotPasswordRequest },
        };
        return this.client.sendRequest(request);
    }

    public async sendUpdatePasswordRequest(
        updatePasswordRequest: IUpdatePasswordPayload,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/updatePassword`;

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
            },
            data: { ...updatePasswordRequest },
            validateStatus: this.validateLockedForbiddenAndUnauthorizedStatus,
        };
        return this.client.sendRequest(request);
    }

    /**
     * Retrieves the dealer names data
     * @param dealerId DealerId for dealers users
     */
    public async getDealerNames(dealerIds: [string]): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getDealerNames`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { dealerIds, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Retrieves the dealer names data by district
     * @param districtId DistrictId for get dealers users
     */
    public async getDealerNamesByDistrict(
        districtId: number,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getDealerIdsByDistrictRegion`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { regionCode: districtId, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    public async unlockUser(
        email: string,
        userId: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/unlockUser`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { email, userId, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Send the dealer id to be activated
     * @param dealerId DealerId
     */
    public async activateDealer(dealerId: string): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/activateDealer`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { dealerId, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Send the dealer id to be get Dealer Group
     * @param dealerId DealerId
     */
    public async getDealerGroups(dealerId: string): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getDealerGroupData`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { dealerId, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Gets all districts and regions
     */
    public async getDistrictsAndRegions(): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getDistrictsAndRegions`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Gets all active regions for distribution rule creation.
     */
    public async getAllActiveRegions(): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getAllActiveRegions`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { jwtToken: permissionsToken },
        };

        return this.client.sendRequest(request);
    }

    /**
     * Gets districts by region org unit id for distribution rule creation.
     */
    public async getDistrictsByRegion(
        orgUnitIds: Array<string | number>,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getDistrictsByRegion`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { orgUnitIds, jwtToken: permissionsToken },
        };

        return this.client.sendRequest(request);
    }

    /**
     * Gets dealership ids by state/province for distribution rule creation.
     */
    public async getDealerIdsByState(state: string): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getDealerIdsByState`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { state, jwtToken: permissionsToken },
        };

        return this.client.sendRequest(request);
    }

    public async saveDistributionRule(
        payload: ISaveDistributionRulePayload,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/saveDistributionRule`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                ...payload,
                jwtToken: permissionsToken,
            },
        };

        return this.client.sendRequest(request);
    }

    /**
     *
     * @param searchType
     * @param searchValue
     */
    public async getDealersBySearchType(
        searchType: string,
        searchValue: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getDealersBySearchType`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { searchType, searchValue, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Gets dealers for internal security management based on search type and value
     * @param searchType
     * @param searchValue
     */
    public async getDealersForISM(
        searchType: string,
        searchValue: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getDealersForISM`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { searchType, searchValue, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    /**
     * @param users
     */
    public async deleteDSMUsers(
        users: Array<{
            username: string;
            userId: string;
        }>,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/deleteDealerUsersDSM`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { users, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    public async migrateUserDSMWithDealerId(
        sourceDealerId: string,
        targetDealerId: string,
        users: Array<{ userId: string }>,
        preservePermissions: boolean,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/migrateUsers`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                sourceDealerId,
                targetDealerId,
                preservePermissions,
                users,
                jwtToken: permissionsToken,
            },
        };
        return this.client.sendRequest(request);
    }

    public async migrateUserDSM(
        targetDealerId: string,
        users: Array<{ userId: string }>,
        preservePermissions: boolean,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/migrateUsers`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                targetDealerId,
                preservePermissions,
                users,
                jwtToken: permissionsToken,
            },
        };
        return this.client.sendRequest(request);
    }

    /**
     * Send the dealer id to be activated
     * @param dealerId DealerId
     */
    public async getAllDocuments(documentType: number): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getAllDocuments`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { documentType, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }

    private validateLockedForbiddenAndUnauthorizedStatus(status: number) {
        return (
            status == HttpStatusCode.UNAUTHORIZED ||
            status == HttpStatusCode.FORBIDDEN ||
            status == HttpStatusCode.LOCKED ||
            (status >= HttpStatusCode.SUCCESS &&
                status < HttpStatusCode.MULTIPLE_CHOICES)
        );
    }

    public async updateDealerGroupPermissions(
        dealerPermissions: Array<{
            dealerId: string;
            currentPermissions: string[];
            newPermissions: string[];
        }>,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/updateDealerPermissions`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                dealerPermissions,
                jwtToken: permissionsToken,
            },
        };
        return this.client.sendRequest(request);
    }

    /*
     * Retrieves all active critical news for the dealer based on the distribution rule.
     * @param payload IGetAllActiveNews object
     */
    public async getAllActiveNews(
        payload: IGetAllActiveNews,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getAllActiveNewsIfCanSeeDistrRule`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: { ...payload, jwtToken: permissionsToken },
        };
        return this.client.sendRequest(request);
    }
}

export interface ISaveDistributionRulePayload {
    name: string;
    country: string;
    brand: string;
    states: string[];
    regions: string[];
    districts: string[];
    dealers: string[];
    key?: string;
}

export interface IAddFieldUserPayload {
    firstName: string;
    lastName: string;
    lastFourOfSsn?: string;
    emailAddress: string;
    phoneNumber: string;
    isFieldAccessUser: boolean;
    country: string;
    regions?: Array<IDealerRegion>;
    districts?: Array<IDealerDistrict>;
}
export interface IUpdateFieldUserPayload {
    firstName: string;
    lastName: string;
    emailAddress: string;
    currentEmailAddress: string;
    userId: string;
    phoneNumber: string;
    isFieldAccessUser: boolean;
    country: string;
    newRegions?: Array<IDealerRegion>;
    newDistricts?: Array<IDealerDistrict>;
}
export interface IAddDealerUserPayload {
    firstName: string;
    lastName: string;
    lastFourOfSsn?: string;
    dealerId: string;
    emailAddress: string;
    phoneNumber: string;
    isFieldAccessUser: boolean;
    country: string;
    permissions: Array<IPermission>;
    associations: Array<IDealerAssociation>;
    regions?: Array<IDealerRegion>;
    districts?: Array<IDealerDistrict>;
}

export interface IPermission {
    dealerId: string | null;
    permission: string;
    modifiedBy?: string;
    modifiedTime?: string;
}

export interface IDealerAssociation {
    dealerId: string;
}

interface IDealerRegion {
    regionId: number;
}

interface IDealerDistrict {
    districtId: number;
}

export interface IEditDealerUserPayload {
    firstName: string;
    lastName: string;
    emailAddress: string;
    currentEmailAddress: string;
    userId: string;
    phoneNumber: string;
    country: string;
    newPermissions: Array<IPermission>;
    newAssociations: Array<IDealerAssociation>;
    newRegions?: Array<IDealerRegion>;
    newDistricts?: Array<IDealerDistrict>;
    isFieldUser?: boolean;
    updateMode?: string;
}

export interface ISendPasswordResetLinkPayload {
    userId: string;
}

export interface IForgotPasswordPayload {
    username: string;
    reToken: string;
}

export interface IUpdatePasswordPayload {
    newPassword: string;
    token: string;
    mfaToken?: string;
}

export interface IGetAllActiveNews {
    dealer: string;
    brand: string;
    region: string;
    district: string;
    state: string;
}
